import { SaxesParser } from 'saxes';
import { F64Buf, U32Buf, U8Buf } from './buffers';
import { num, parseDoubles, parseTokens, looksCommaDecimal } from './num';
import { angleToRad, asAngUnit, defaultUnits, linearToMeters } from './units';
import {
  type Alignment, type AlignPI, type Bounds, type CgElement, type CgPoint, type CoordGeom, type CrossSect,
  type CrossSectSurf, type Crs, type FeatureNode, type LandXmlDoc, type Monument,
  type Parcel, type PipeNetwork, type PipeRun, type PipeStruct, type PlanFeature,
  type ProfAlign, type ProfileDef, type ProfSurf, type Pt, type Pt2, type PviEntry,
  type Rot, type SurfPolyline, type Surface, type Units, type Warning,
  emptyBounds, growBounds,
} from './types';

export interface ParseOptions {
  /**
   * Read point text as `easting northing` instead of the schema's
   * `northing easting`. A handful of exporters get this wrong; the viewer
   * exposes it as a toggle.
   */
  swapNE?: boolean;
  fileName?: string;
}

interface Ctx {
  name: string;
  attrs: Record<string, string>;
  text: string[] | null;
  data?: unknown;
}

/** `<IrregularLine>` carries both Start/End points and a bulk point list. */
interface IrregData { start?: Pt; end?: Pt; list?: Pt2[] }

interface SurfState {
  name: string;
  desc?: string;
  surfType: 'TIN' | 'grid';
  xyz: F64Buf;
  dense: boolean;
  count: number;
  idMap: Map<number, number> | null;
  faceIds: U32Buf;
  faceVis: U8Buf;
  breaklines: SurfPolyline[];
  boundaries: SurfPolyline[];
  contours: SurfPolyline[];
  attrs: Record<string, string>;
  droppedFaces: number;
}

const NUMERIC_TEXT = new Set([
  'P', 'F', 'CgPoint', 'Start', 'End', 'Center', 'PI', 'PntList2D', 'PntList3D',
  'PVI', 'ParaCurve', 'UnsymParaCurve', 'CircCurve', 'Chain', 'Location',
  'MapPoint', 'TargetPoint', 'InstrumentPoint', 'Invert', 'Property',
  'Station', 'CrossSectPnt',
]);

/** `<AlignPI>` wrapper element -> field on AlignPI. */
const PI_SLOTS: Record<string, keyof AlignPI> = {
  InSpiral: 'inSpiral',
  Curve1: 'curve1',
  MidSpiral: 'midSpiral',
  Curve2: 'curve2',
  OutSpiral: 'outSpiral',
};

export function parseLandXml(xml: string, opts: ParseOptions = {}): LandXmlDoc {
  const swap = !!opts.swapNE;
  const warnMap = new Map<string, Warning>();
  const bounds: Bounds = emptyBounds();

  function warn(level: Warning['level'], code: string, message: string): void {
    const w = warnMap.get(code);
    if (w) { w.count++; return; }
    warnMap.set(code, { level, code, message, count: 1 });
  }

  let units: Units = defaultUnits();
  let crs: Crs | undefined;
  let version = '';
  let namespace: string | undefined;
  let date: string | undefined;
  let time: string | undefined;
  let language: string | undefined;
  let project: LandXmlDoc['project'];
  let application: LandXmlDoc['application'];

  const cgPoints: CgPoint[] = [];
  const cgPointsByName = new Map<string, CgPoint>();
  const surfaces: Surface[] = [];
  const alignments: Alignment[] = [];
  const parcels: Parcel[] = [];
  const planFeatures: PlanFeature[] = [];
  const pipeNetworks: PipeNetwork[] = [];
  const monuments: Monument[] = [];
  const docFeatures: FeatureNode[] = [];

  /** Points carrying only a `pntRef`; resolved once every CgPoint is known. */
  const pendingRefs: Pt[] = [];

  const stack: Ctx[] = [];
  const top = (): Ctx | undefined => stack[stack.length - 1];

  // --- per-section accumulators -------------------------------------------
  let surf: SurfState | null = null;
  let align: Alignment | null = null;
  let cg: CoordGeom | null = null;
  let cgStack: CoordGeom[] = [];
  let profile: ProfileDef | null = null;
  let profAlign: ProfAlign | null = null;
  let profSurf: ProfSurf | null = null;
  let crossSects: CrossSect[] | null = null;
  let crossSect: CrossSect | null = null;
  let crossSurf: CrossSectSurf | null = null;
  let parcelStack: Parcel[] = [];
  let planFeature: PlanFeature | null = null;
  let planGroupName: string | undefined;
  let pipeNet: PipeNetwork | null = null;
  let pipeStruct: PipeStruct | null = null;
  let pipeRun: PipeRun | null = null;
  let featureStack: FeatureNode[] = [];
  let cgPointGroupPath: string[] = [];
  let alignPIs: AlignPI[] | null = null;
  let alignPI: AlignPI | null = null;
  let piSlot: keyof AlignPI | null = null;

  // --- helpers ------------------------------------------------------------

  function localName(qname: string): string {
    const i = qname.indexOf(':');
    return i < 0 ? qname : qname.slice(i + 1);
  }

  /** Turn LandXML point text (`northing easting [elev]`) into survey-plane x/y/z. */
  function toXY(v: number[]): { x?: number; y?: number; z?: number } {
    if (v.length >= 3) {
      return swap ? { x: v[0], y: v[1], z: v[2] } : { x: v[1], y: v[0], z: v[2] };
    }
    if (v.length === 2) {
      return swap ? { x: v[0], y: v[1] } : { x: v[1], y: v[0] };
    }
    return {};
  }

  function makePt(ctx: Ctx): Pt {
    const vals = parseDoubles(ctx.text ? ctx.text.join('') : '');
    const p: Pt = toXY(vals);
    const ref = ctx.attrs.pntRef;
    if (ref) p.pntRef = ref.trim();
    if (p.x === undefined && p.pntRef) pendingRefs.push(p);
    if (p.x !== undefined && p.y !== undefined) growBounds(bounds, p.x, p.y, p.z);
    return p;
  }

  /** `PntList2D` / `PntList3D` in a *plan* context (north/east[/elev]). */
  function makePtList(ctx: Ctx): { pts: Pt2[]; flat: boolean } {
    const v = parseDoubles(ctx.text ? ctx.text.join('') : '');
    const three = ctx.name === 'PntList3D';
    const stride = three ? 3 : 2;
    const pts: Pt2[] = [];
    for (let i = 0; i + stride <= v.length; i += stride) {
      const c = toXY(three ? [v[i], v[i + 1], v[i + 2]] : [v[i], v[i + 1]]);
      if (c.x === undefined || c.y === undefined) continue;
      const p: Pt2 = { x: c.x, y: c.y };
      if (c.z !== undefined) p.z = c.z;
      pts.push(p);
      growBounds(bounds, p.x, p.y, p.z);
    }
    return { pts, flat: !three };
  }

  /** `PntList2D` in a profile/section context: raw `[a, b]` pairs, never swapped. */
  function makePairs(ctx: Ctx): Array<[number, number]> {
    const v = parseDoubles(ctx.text ? ctx.text.join('') : '');
    const out: Array<[number, number]> = [];
    for (let i = 0; i + 2 <= v.length; i += 2) out.push([v[i], v[i + 1]]);
    return out;
  }

  function ang(v: string | undefined): number | undefined {
    const n = num(v);
    return n === undefined ? undefined : angleToRad(n, units.angular);
  }
  function dir(v: string | undefined): number | undefined {
    const n = num(v);
    return n === undefined ? undefined : angleToRad(n, units.direction);
  }
  function rotOf(v: string | undefined): Rot {
    return (v ?? '').trim().toLowerCase() === 'ccw' ? 'ccw' : 'cw';
  }

  function currentCoordGeom(): CoordGeom | null { return cg; }

  function pushCgElement(el: CgElement): void {
    if (alignPI && piSlot) {
      // Inside <AlignPIs>: the Curve/Spiral belongs to a PI slot, not a CoordGeom.
      (alignPI as unknown as Record<string, unknown>)[piSlot as string] = el;
      return;
    }
    const g = currentCoordGeom();
    if (g) g.elements.push(el);
  }

  /** A spiral radius of 0 means "tangent" in several writers; the schema spells it INF. */
  function spiralRadius(v: string | undefined): number {
    const r = num(v);
    if (r === undefined) return Infinity;
    if (r === 0) {
      warn('info', 'spiral.radius.zero', 'Spiral radius written as 0; read as infinite (a tangent end)');
      return Infinity;
    }
    return r;
  }

  function attachFeature(f: FeatureNode): void {
    if (featureStack.length) { featureStack[featureStack.length - 1].children.push(f); return; }
    if (align) { align.features.push(f); return; }
    docFeatures.push(f);
  }

  // --- surface helpers ----------------------------------------------------

  function surfPushP(s: SurfState, id: number, x: number, y: number, z: number): void {
    s.count++;
    if (s.dense) {
      if (id !== s.count) {
        s.idMap = new Map();
        for (let i = 1; i < s.count; i++) s.idMap.set(i, i - 1);
        s.idMap.set(id, s.count - 1);
        s.dense = false;
      }
    } else {
      s.idMap!.set(id, s.count - 1);
    }
    s.xyz.push3(x, y, z);
  }

  function surfIndex(s: SurfState, id: number): number {
    if (s.dense) return id >= 1 && id <= s.count ? id - 1 : -1;
    const v = s.idMap!.get(id);
    return v === undefined ? -1 : v;
  }

  function finishSurface(s: SurfState): void {
    const xyz = s.xyz.toArray();
    const tris = new U32Buf(Math.max(3, s.faceIds.length));
    const vis = new U8Buf(Math.max(1, s.faceIds.length / 3));
    const raw = s.faceIds.toArray();
    const rawVis = s.faceVis.toArray();
    for (let f = 0, k = 0; f + 3 <= raw.length; f += 3, k++) {
      const a = surfIndex(s, raw[f]);
      const b = surfIndex(s, raw[f + 1]);
      const c = surfIndex(s, raw[f + 2]);
      if (a < 0 || b < 0 || c < 0) { s.droppedFaces++; continue; }
      tris.push3(a, b, c);
      vis.push(rawVis[k]);
    }
    if (s.droppedFaces) {
      warn('warn', 'surface.face.unresolved',
        `${s.droppedFaces} surface face(s) referenced point ids that do not exist and were dropped`);
    }
    let zmin = Infinity, zmax = -Infinity;
    for (let i = 2; i < xyz.length; i += 3) {
      const z = xyz[i];
      if (z < zmin) zmin = z;
      if (z > zmax) zmax = z;
    }
    for (let i = 0; i < xyz.length; i += 3) growBounds(bounds, xyz[i], xyz[i + 1], xyz[i + 2]);
    surfaces.push({
      name: s.name,
      desc: s.desc,
      surfType: s.surfType,
      xyz,
      tris: tris.toArray(),
      triVisible: vis.toArray(),
      elevMin: num(s.attrs.elevMin) ?? (Number.isFinite(zmin) ? zmin : 0),
      elevMax: num(s.attrs.elevMax) ?? (Number.isFinite(zmax) ? zmax : 0),
      area2DSurf: num(s.attrs.area2DSurf),
      area3DSurf: num(s.attrs.area3DSurf),
      breaklines: s.breaklines,
      boundaries: s.boundaries,
      contours: s.contours,
    });
  }

  // --- SAX wiring ---------------------------------------------------------

  const parser = new SaxesParser({ xmlns: false, fileName: opts.fileName });

  parser.on('error', (e) => {
    warn('error', 'xml.parse', `XML parse error: ${e.message}`);
  });

  parser.on('opentag', (tag) => {
    const name = localName(tag.name);
    const attrs = tag.attributes as Record<string, string>;
    const ctx: Ctx = { name, attrs, text: NUMERIC_TEXT.has(name) ? [] : null };
    stack.push(ctx);

    switch (name) {
      case 'LandXML': {
        version = attrs.version ?? '';
        date = attrs.date; time = attrs.time; language = attrs.language;
        for (const [k, v] of Object.entries(attrs)) {
          if (k === 'xmlns' || k.startsWith('xmlns:')) {
            if (/landxml\.org\/schema/i.test(v)) namespace = v;
          }
        }
        break;
      }
      case 'Metric':
      case 'Imperial': {
        const system = name === 'Metric' ? 'metric' : 'imperial';
        if (units.raw.linearUnit && units.system !== system) {
          warn('warn', 'units.both', 'File declares both Metric and Imperial units; using the first');
          break;
        }
        units = {
          system,
          linear: attrs.linearUnit ?? (system === 'metric' ? 'meter' : 'foot'),
          area: attrs.areaUnit,
          volume: attrs.volumeUnit,
          angular: asAngUnit(attrs.angularUnit, 'radians'),
          direction: asAngUnit(attrs.directionUnit, asAngUnit(attrs.angularUnit, 'radians')),
          latLong: attrs.latLongAngularUnit ? asAngUnit(attrs.latLongAngularUnit) : undefined,
          linearToMeters: linearToMeters(attrs.linearUnit),
          raw: { ...attrs },
        };
        break;
      }
      case 'CoordinateSystem': {
        crs = {
          name: attrs.name || undefined,
          epsgCode: attrs.epsgCode || undefined,
          ogcWktCode: attrs.ogcWktCode || undefined,
          rotationAngle: ang(attrs.rotationAngle),
          horizontalDatum: attrs.horizontalDatum || undefined,
          verticalDatum: attrs.verticalDatum || undefined,
          ellipsoidName: attrs.ellipsoidName || undefined,
          projectedCoordinateSystemName: attrs.projectedCoordinateSystemName || undefined,
          geographicCoordinateSystemName: attrs.geographicCoordinateSystemName || undefined,
          horizontalCoordinateSystemName: attrs.horizontalCoordinateSystemName || undefined,
          verticalCoordinateSystemName: attrs.verticalCoordinateSystemName || undefined,
          raw: { ...attrs },
        };
        break;
      }
      case 'Project':
        project = { name: attrs.name, desc: attrs.desc };
        break;
      case 'Application':
        application = { name: attrs.name, version: attrs.version, manufacturer: attrs.manufacturer };
        break;

      // ---- CgPoints -----------------------------------------------------
      case 'CgPoints':
        cgPointGroupPath.push(attrs.name ?? attrs.desc ?? '');
        break;

      // ---- Surfaces -----------------------------------------------------
      case 'Surface':
        surf = {
          name: attrs.name ?? `Surface ${surfaces.length + 1}`,
          desc: attrs.desc,
          surfType: 'TIN',
          xyz: new F64Buf(3 * 4096),
          dense: true,
          count: 0,
          idMap: null,
          faceIds: new U32Buf(3 * 4096),
          faceVis: new U8Buf(4096),
          breaklines: [], boundaries: [], contours: [],
          attrs: {},
          droppedFaces: 0,
        };
        break;
      case 'Definition':
        if (surf) {
          surf.surfType = (attrs.surfType ?? 'TIN').trim() === 'grid' ? 'grid' : 'TIN';
          surf.attrs = { ...surf.attrs, ...attrs };
        }
        break;

      // ---- Alignments ---------------------------------------------------
      case 'Alignment':
        align = {
          name: attrs.name ?? `Alignment ${alignments.length + 1}`,
          desc: attrs.desc,
          length: num(attrs.length) ?? 0,
          staStart: num(attrs.staStart) ?? 0,
          state: attrs.state,
          coordGeom: { elements: [] },
          alignPIs: [],
          staEquations: [],
          profiles: [],
          crossSects: [],
          features: [],
        };
        break;
      case 'AlignPIs':
        alignPIs = [];
        break;
      case 'AlignPI':
        alignPI = { pi: {} };
        break;
      case 'InSpiral': case 'Curve1': case 'MidSpiral': case 'Curve2': case 'OutSpiral':
        piSlot = PI_SLOTS[name];
        break;
      case 'CoordGeom': {
        const g: CoordGeom = { name: attrs.name, desc: attrs.desc, elements: [] };
        cgStack.push(g);
        cg = g;
        break;
      }
      case 'Profile':
        profile = { name: attrs.name, staStart: num(attrs.staStart), surfaces: [], aligns: [] };
        break;
      case 'ProfSurf':
        profSurf = { name: attrs.name ?? 'surface', runs: [] };
        break;
      case 'ProfAlign':
        profAlign = { name: attrs.name ?? 'layout', entries: [] };
        break;
      case 'CrossSects':
        crossSects = [];
        break;
      case 'CrossSect':
        crossSect = {
          sta: num(attrs.sta) ?? 0,
          name: attrs.name,
          desc: attrs.desc,
          areaCut: num(attrs.areaCut),
          areaFill: num(attrs.areaFill),
          surfs: [],
        };
        break;
      case 'CrossSectSurf':
      case 'DesignCrossSectSurf':
        crossSurf = {
          name: attrs.name ?? 'surf',
          pts: [],
          design: name === 'DesignCrossSectSurf',
          side: attrs.side,
          material: attrs.material,
        };
        break;
      case 'Cant':
        if (align) align.cant = { name: attrs.name, gauge: num(attrs.gauge), stations: [] };
        break;
      case 'CantStation':
        if (align?.cant) {
          align.cant.stations.push({
            station: num(attrs.station) ?? 0,
            appliedCant: num(attrs.appliedCant),
            speed: num(attrs.speed),
            curvature: attrs.curvature ? rotOf(attrs.curvature) : undefined,
          });
        }
        break;
      case 'StaEquation':
        if (align) {
          align.staEquations.push({
            staInternal: num(attrs.staInternal) ?? 0,
            staAhead: num(attrs.staAhead) ?? 0,
            staBack: num(attrs.staBack),
            increasing: (attrs.staIncrement ?? 'increasing').toLowerCase() !== 'decreasing',
          });
        }
        break;

      // ---- Parcels / plan features --------------------------------------
      case 'Parcel': {
        const p: Parcel = {
          name: attrs.name ?? `Parcel ${parcels.length + 1}`,
          desc: attrs.desc,
          parcelType: attrs.parcelType,
          state: attrs.state,
          area: num(attrs.area),
          owner: attrs.owner,
          children: [],
        };
        parcelStack.push(p);
        break;
      }
      case 'PlanFeatures':
        planGroupName = attrs.name ?? attrs.desc;
        break;
      case 'PlanFeature':
        planFeature = {
          name: attrs.name,
          desc: attrs.desc,
          group: planGroupName,
          coordGeoms: [],
          locations: [],
        };
        break;

      // ---- Pipes / monuments --------------------------------------------
      case 'PipeNetwork':
        pipeNet = {
          name: attrs.name ?? `Network ${pipeNetworks.length + 1}`,
          pipeNetType: attrs.pipeNetType,
          structs: [], pipes: [],
        };
        break;
      case 'Struct':
        pipeStruct = {
          name: attrs.name ?? '?',
          desc: attrs.desc,
          elevRim: num(attrs.elevRim),
          elevSump: num(attrs.elevSump),
        };
        break;
      case 'CircStruct':
      case 'RectStruct':
      case 'InletStruct':
      case 'OutletStruct':
        if (pipeStruct) {
          pipeStruct.shape = name;
          pipeStruct.diameter = num(attrs.diameter);
          pipeStruct.width = num(attrs.width);
          pipeStruct.length = num(attrs.length);
        }
        break;
      case 'Pipe':
        pipeRun = {
          name: attrs.name ?? '?',
          refStart: attrs.refStart,
          refEnd: attrs.refEnd,
          length: num(attrs.length),
          slope: num(attrs.slope),
        };
        break;
      case 'CircPipe':
      case 'RectPipe':
      case 'EggPipe':
      case 'ElliPipe':
      case 'Channel':
        if (pipeRun) {
          pipeRun.shape = name;
          pipeRun.diameter = num(attrs.diameter);
          pipeRun.material = attrs.material;
        }
        break;
      case 'Monument':
        monuments.push({
          name: attrs.name ?? '?',
          pntRef: attrs.pntRef,
          desc: attrs.desc,
          type: attrs.type,
          state: attrs.state,
          condition: attrs.condition,
        });
        break;

      // ---- Extension mechanism ------------------------------------------
      case 'Feature':
        featureStack.push({ code: attrs.code, source: attrs.source, name: attrs.name, props: {}, children: [] });
        break;
      case 'Property':
        if (featureStack.length && attrs.label !== undefined) {
          featureStack[featureStack.length - 1].props[attrs.label] = attrs.value ?? '';
        }
        break;
      default:
        break;
    }
  });

  parser.on('text', (t) => {
    const c = top();
    if (c && c.text) c.text.push(t);
  });

  parser.on('closetag', () => {
    const ctx = stack.pop();
    if (!ctx) return;
    const { name, attrs } = ctx;

    switch (name) {
      // ---- points -------------------------------------------------------
      case 'CgPoint': {
        const vals = parseDoubles(ctx.text ? ctx.text.join('') : '');
        const c = toXY(vals);
        const nm = attrs.name ?? attrs.oID ?? `p${cgPoints.length + 1}`;
        if (c.x === undefined || c.y === undefined) {
          warn('warn', 'cgpoint.nocoord', `CgPoint "${nm}" has no usable coordinates`);
          break;
        }
        const extra: Record<string, string> = {};
        for (const [k, v] of Object.entries(attrs)) {
          if (!['name', 'code', 'desc', 'state', 'timeStamp', 'oID', 'role', 'pntRef'].includes(k)) extra[k] = v;
        }
        const p: CgPoint = {
          name: nm, x: c.x, y: c.y, z: c.z,
          code: attrs.code || undefined,
          desc: attrs.desc || undefined,
          state: attrs.state || undefined,
          timeStamp: attrs.timeStamp || undefined,
          oID: attrs.oID || undefined,
          role: attrs.role || undefined,
          extra: Object.keys(extra).length ? extra : undefined,
        };
        cgPoints.push(p);
        if (cgPointsByName.has(nm)) {
          warn('warn', 'cgpoint.duplicate', `Duplicate CgPoint name(s) such as "${nm}"; the last one wins`);
        }
        cgPointsByName.set(nm, p);
        growBounds(bounds, p.x, p.y, p.z);
        break;
      }
      case 'CgPoints':
        cgPointGroupPath.pop();
        break;

      // ---- surface ------------------------------------------------------
      case 'P': {
        if (!surf) break;
        const id = num(attrs.id);
        const vals = parseDoubles(ctx.text ? ctx.text.join('') : '');
        const c = toXY(vals);
        if (id === undefined || c.x === undefined || c.y === undefined) {
          warn('warn', 'surface.point.bad', 'Surface point missing id or coordinates; skipped');
          break;
        }
        if (c.z === undefined) warn('warn', 'surface.point.noz', 'Surface point without elevation; assumed 0');
        surfPushP(surf, id, c.x, c.y, c.z ?? 0);
        break;
      }
      case 'F': {
        if (!surf) break;
        const ids = parseDoubles(ctx.text ? ctx.text.join('') : '');
        const invisible = (num(attrs.i) ?? 0) !== 0 ? 0 : 1;
        if (ids.length === 3) {
          surf.faceIds.push3(ids[0], ids[1], ids[2]);
          surf.faceVis.push(invisible);
        } else if (ids.length === 4) {
          surf.faceIds.push3(ids[0], ids[1], ids[2]);
          surf.faceVis.push(invisible);
          surf.faceIds.push3(ids[0], ids[2], ids[3]);
          surf.faceVis.push(invisible);
        } else {
          surf.droppedFaces++;
        }
        break;
      }
      case 'Surface':
        if (surf) finishSurface(surf);
        surf = null;
        break;

      // ---- point lists --------------------------------------------------
      case 'PntList2D':
      case 'PntList3D': {
        const parent = top();
        const pname = parent?.name ?? '';
        if (pname === 'ProfSurf' && profSurf) {
          const pairs = makePairs(ctx);
          if (pairs.length) profSurf.runs.push(pairs);
          break;
        }
        if ((pname === 'CrossSectSurf' || pname === 'DesignCrossSectSurf') && crossSurf) {
          crossSurf.pts.push(...makePairs(ctx));
          break;
        }
        const { pts, flat } = makePtList(ctx);
        if (pname === 'Breakline' && surf) {
          surf.breaklines.push({ name: parent?.attrs.name, type: parent?.attrs.brkType, pts, flat });
        } else if (pname === 'Boundary' && surf) {
          surf.boundaries.push({ name: parent?.attrs.name, type: parent?.attrs.bndType, pts, flat });
        } else if (pname === 'Contour' && surf) {
          surf.contours.push({ name: parent?.attrs.name, type: parent?.attrs.elev, pts, flat });
        } else if (pname === 'IrregularLine') {
          const d = (parent!.data as IrregData | undefined) ?? {};
          d.list = (d.list ?? []).concat(pts);
          parent!.data = d;
        }
        break;
      }

      // ---- CoordGeom elements -------------------------------------------
      case 'Start': case 'End': case 'Center': case 'PI': {
        const parent = top();
        if (!parent) break;
        const p = makePt(ctx);
        const slot = name.toLowerCase() === 'pi' ? 'pi' : name.toLowerCase();
        const d = (parent.data as Record<string, unknown> | undefined) ?? {};
        d[slot] = p;
        parent.data = d;
        break;
      }
      case 'Line': {
        const d = (ctx.data as Record<string, Pt> | undefined) ?? {};
        pushCgElement({
          kind: 'Line', name: attrs.name, desc: attrs.desc,
          staStart: num(attrs.staStart), length: num(attrs.length),
          dir: dir(attrs.dir),
          start: d.start ?? {}, end: d.end ?? {},
        });
        break;
      }
      case 'Curve': {
        const d = (ctx.data as Record<string, Pt> | undefined) ?? {};
        pushCgElement({
          kind: 'Curve', name: attrs.name, desc: attrs.desc,
          staStart: num(attrs.staStart), length: num(attrs.length),
          rot: rotOf(attrs.rot),
          start: d.start ?? {}, end: d.end ?? {}, center: d.center, pi: d.pi,
          radius: num(attrs.radius),
          delta: ang(attrs.delta),
          chord: num(attrs.chord),
          crvType: attrs.crvType === 'chord' ? 'chord' : attrs.crvType === 'arc' ? 'arc' : undefined,
          dirStart: dir(attrs.dirStart), dirEnd: dir(attrs.dirEnd),
          tangent: num(attrs.tangent), external: num(attrs.external), midOrd: num(attrs.midOrd),
        });
        break;
      }
      case 'Spiral': {
        const d = (ctx.data as Record<string, Pt> | undefined) ?? {};
        pushCgElement({
          kind: 'Spiral', name: attrs.name, desc: attrs.desc,
          staStart: num(attrs.staStart),
          length: num(attrs.length) ?? 0,
          rot: rotOf(attrs.rot),
          spiType: (attrs.spiType ?? 'clothoid').trim(),
          radiusStart: spiralRadius(attrs.radiusStart),
          radiusEnd: spiralRadius(attrs.radiusEnd),
          start: d.start ?? {}, end: d.end ?? {}, pi: d.pi,
          theta: ang(attrs.theta),
          totalX: num(attrs.totalX), totalY: num(attrs.totalY),
          tanLong: num(attrs.tanLong), tanShort: num(attrs.tanShort),
          constant: num(attrs.constant),
          dirStart: dir(attrs.dirStart), dirEnd: dir(attrs.dirEnd),
        });
        break;
      }
      case 'IrregularLine': {
        const d = (ctx.data as IrregData | undefined) ?? {};
        const list: Pt2[] = d.list ?? [];
        const s = d.start; const e = d.end;
        pushCgElement({
          kind: 'IrregularLine', name: attrs.name, desc: attrs.desc,
          staStart: num(attrs.staStart), length: num(attrs.length),
          start: s, end: e, pts: list,
        });
        break;
      }
      case 'Chain': {
        const refs = parseTokens(ctx.text ? ctx.text.join('') : '');
        pushCgElement({ kind: 'Chain', name: attrs.name, desc: attrs.desc, refs });
        break;
      }
      case 'InSpiral': case 'Curve1': case 'MidSpiral': case 'Curve2': case 'OutSpiral':
        piSlot = null;
        break;
      case 'Station': {
        const v = parseDoubles(ctx.text ? ctx.text.join('') : '');
        if (alignPI && v.length) alignPI.station = v[0];
        break;
      }
      case 'AlignPI': {
        if (alignPI && alignPIs) {
          const d = ctx.data as Record<string, Pt> | undefined;
          if (d?.pi) alignPI.pi = d.pi;
          alignPI.code = attrs.code;
          alignPI.name = attrs.name;
          alignPIs.push(alignPI);
        }
        alignPI = null;
        break;
      }
      case 'AlignPIs':
        if (align && alignPIs) align.alignPIs = alignPIs;
        alignPIs = null;
        break;
      case 'CrossSectPnt': {
        if (!crossSurf) break;
        const v = parseDoubles(ctx.text ? ctx.text.join('') : '');
        if (attrs.dataFormat) crossSurf.dataFormat = attrs.dataFormat;
        if (v.length >= 2) crossSurf.pts.push([v[0], v[1]]);
        break;
      }
      case 'CoordGeom': {
        const g = cgStack.pop();
        cg = cgStack.length ? cgStack[cgStack.length - 1] : null;
        if (!g) break;
        const parent = top()?.name;
        if (parent === 'Alignment' && align) align.coordGeom = g;
        else if (parent === 'Parcel' && parcelStack.length) parcelStack[parcelStack.length - 1].coordGeom = g;
        else if (parent === 'PlanFeature' && planFeature) planFeature.coordGeoms.push(g);
        else if (align && align.coordGeom.elements.length === 0) align.coordGeom = g;
        break;
      }

      // ---- vertical -----------------------------------------------------
      case 'PVI': case 'ParaCurve': case 'UnsymParaCurve': case 'CircCurve': {
        if (!profAlign) break;
        const v = parseDoubles(ctx.text ? ctx.text.join('') : '');
        if (v.length < 2) { warn('warn', 'profile.pvi.bad', `${name} without "station elevation" text; skipped`); break; }
        const e: PviEntry = {
          kind: name as PviEntry['kind'],
          sta: v[0], elev: v[1],
          length: num(attrs.length),
          lengthIn: num(attrs.lengthIn),
          lengthOut: num(attrs.lengthOut),
          radius: num(attrs.radius),
          desc: attrs.desc,
        };
        profAlign.entries.push(e);
        break;
      }
      case 'ProfAlign':
        if (profile && profAlign) profile.aligns.push(profAlign);
        profAlign = null;
        break;
      case 'ProfSurf':
        if (profile && profSurf) profile.surfaces.push(profSurf);
        profSurf = null;
        break;
      case 'Profile':
        if (profile && align) align.profiles.push(profile);
        profile = null;
        break;
      case 'CrossSectSurf':
      case 'DesignCrossSectSurf':
        if (crossSect && crossSurf) crossSect.surfs.push(crossSurf);
        crossSurf = null;
        break;
      case 'CrossSect':
        if (crossSects && crossSect) crossSects.push(crossSect);
        crossSect = null;
        break;
      case 'CrossSects':
        if (align && crossSects) align.crossSects = crossSects;
        crossSects = null;
        break;
      case 'Alignment':
        if (align) {
          // Some writers (SDMS) put an equation *index* in staInternal rather than a
          // station. Dropping out-of-range equations keeps station labels sane.
          const lo = align.staStart - 1e-6;
          const hi = align.staStart + (align.length || Infinity) + 1e-6;
          const kept = align.staEquations.filter((e) => e.staInternal >= lo && e.staInternal <= hi);
          if (kept.length !== align.staEquations.length) {
            warn('info', 'staequation.range',
              `Alignment "${align.name}": ${align.staEquations.length - kept.length} of ${align.staEquations.length} station equations sit outside this alignment's own station range and are not applied to its labels. That is normal when the file stores the equations for a whole route on a sub-length alignment, and it also protects against writers that put an equation index in staInternal.`);
            align.staEquations = kept;
          }
          alignments.push(align);
        }
        align = null;
        break;

      // ---- parcels / plan features --------------------------------------
      case 'Parcel': {
        const p = parcelStack.pop();
        if (!p) break;
        if (parcelStack.length) parcelStack[parcelStack.length - 1].children.push(p);
        else parcels.push(p);
        break;
      }
      case 'PlanFeature':
        if (planFeature) planFeatures.push(planFeature);
        planFeature = null;
        break;
      case 'PlanFeatures':
        planGroupName = undefined;
        break;
      case 'Location': {
        const p = makePt(ctx);
        if (planFeature && p.x !== undefined && p.y !== undefined) {
          planFeature.locations.push({ x: p.x, y: p.y, z: p.z });
        }
        break;
      }

      // ---- pipes --------------------------------------------------------
      case 'Struct':
        if (pipeNet && pipeStruct) {
          const d = ctx.data as Record<string, Pt> | undefined;
          if (d?.center) pipeStruct.center = d.center;
          pipeNet.structs.push(pipeStruct);
        }
        pipeStruct = null;
        break;
      case 'Pipe':
        if (pipeNet && pipeRun) pipeNet.pipes.push(pipeRun);
        pipeRun = null;
        break;
      case 'PipeNetwork':
        if (pipeNet) pipeNetworks.push(pipeNet);
        pipeNet = null;
        break;

      // ---- features -----------------------------------------------------
      case 'Feature': {
        const f = featureStack.pop();
        if (!f) break;
        if (featureStack.length) featureStack[featureStack.length - 1].children.push(f);
        else attachFeature(f);
        break;
      }
      default:
        break;
    }
  });

  // Cheap sanity check for mis-localised exports before we spend time parsing.
  const probe = xml.slice(0, 4096);
  if (looksCommaDecimal(probe)) {
    warn('error', 'num.comma',
      'The file appears to use comma decimal separators, which LandXML does not allow. Numbers may be wrong.');
  }

  try {
    parser.write(xml).close();
  } catch (e) {
    warn('error', 'xml.fatal', `Could not finish parsing: ${(e as Error).message}`);
  }

  // Resolve `pntRef`-only points now that every CgPoint is known.
  let unresolved = 0;
  for (const p of pendingRefs) {
    const src = p.pntRef ? cgPointsByName.get(p.pntRef) : undefined;
    if (!src) { unresolved++; continue; }
    p.x = src.x; p.y = src.y;
    if (p.z === undefined) p.z = src.z;
    growBounds(bounds, p.x, p.y, p.z);
  }
  if (unresolved) {
    warn('warn', 'pntref.unresolved',
      `${unresolved} point reference(s) named a CgPoint that is not in the file`);
  }

  if (!units.raw.linearUnit && !units.raw.areaUnit) {
    warn('warn', 'units.missing', 'No <Units> element; assumed metres and radians');
  }
  if (!crs) {
    warn('info', 'crs.missing', 'No <CoordinateSystem>; pick a CRS manually to enable the basemap');
  }

  return {
    version, namespace, date, time, language, project, application,
    units, crs,
    cgPoints, cgPointsByName,
    surfaces, alignments, parcels, planFeatures, pipeNetworks, monuments,
    features: docFeatures,
    warnings: [...warnMap.values()],
    bounds,
    sourceBytes: xml.length,
    fileName: opts.fileName,
  };
}
